/**
 * Created by Bowen on 10/20/2020.
 * Description:
 * Duplicated contact dialog
 *
 * ------ maintenance history ------
 */

import { Component, Input, OnInit } from '@angular/core';
import { of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';

import { Contact } from '@tamalelibs/models/contact.model';
import { ContactTemplateOpenOptions, ContactTemplateType } from '../../contact-dialog-new/contact-dialog.model';
import { DuplicatedContactDialogService } from '../duplicated-contact-dialog.service';
import { ContactTemplateService } from './../../contact-dialog-new/contact-template.service';
import { ContactService } from '@tamalelibs/services/contact.service';
import { DialogAction } from './../../../widgets/tam-dialog/tam-dialog.model';
import { OpenDynamicComponentService } from '../../../pages/home/home-open-dynamic-component';
import { EntityBack } from '../../entity-dialog/entity-dialog.model';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../../redux';
import { duplicatedContactFeedbackSelector } from '@tamalelibs/redux/contact.reducer';
import { ADD_DUPLICATED_CONTACT } from '@tamalelibs/redux/contact.actions';
import { AddInUtilities } from '../../../services/utilities/addin-utilities';

@Component({
    selector: 'tam-duplicated-item',
    templateUrl: './duplicated-item.component.html',
    styleUrls: ['./duplicated-item.component.scss'],
    standalone: false
})
export class DuplicatedItemComponent implements OnInit {
    @Input() config;

    constructor(
        private _contactService: ContactService,
        private _contactTemplateService: ContactTemplateService,
        private _duplicatedContactDialogService: DuplicatedContactDialogService,
        private _openDynamicComponentService: OpenDynamicComponentService,
        private _store: Store<AppState>,
    ) { }

    ngOnInit() { }

    openContact(config): void {
        // first, need to close the parent dialog
        // we don't need to close the parent contact dialog on the condition that user clicks open contact on the duplicated component in office addin mode.
        if (!AddInUtilities.isOfficeJSLoaded()) {
            this._contactTemplateService.actionSubject$.next({
                type: DialogAction.CloseDialog,
                payload: {
                    instanceId: config.instanceId
                }
            });
        }

        this._contactService.getContactDetail(config.contactId).pipe(
            catchError((err) => {
                return of(null);
            }),
            take(1),
            map(response => {
                return Contact.parse(response, true);
            })).subscribe(response => {
                if (response) {
                    // then, need to open a new edit contact dialog
                    const contactDialogOpenOptions = new ContactTemplateOpenOptions();
                    contactDialogOpenOptions.openType = ContactTemplateType.EditContact;
                    contactDialogOpenOptions.contact = response;
                    contactDialogOpenOptions.instanceId = 'EDIT_CONTACT' + new Date().getTime();

                    // third, need to close the duplicate contact dialog
                    this._duplicatedContactDialogService.dialogOpen$.next({
                        type: DialogAction.CloseDialog,
                        payload: null
                    });

                    const entityBack = new EntityBack();
                    entityBack.controlId = config.controlId;
                    entityBack.entityId = config.contactId;
                    entityBack.searchValue = config.contactName;
                    entityBack.currentId = config.instanceId;

                    contactDialogOpenOptions.entityBack = entityBack;
                    this._openDynamicComponentService.openContactDialog$.next(contactDialogOpenOptions);

                    // here is for note dialog feedback
                    this._store.pipe(
                        select(duplicatedContactFeedbackSelector),
                        take(1),
                    ).subscribe(duplicatedControls => {
                        let isNeedAdded = false;
                        if (duplicatedControls.length > 0) {
                            for (const feedbackItem of duplicatedControls) {
                                // 1. when it's the same control, but not in a same dialog, and the count is equal to 0
                                const condition1 = feedbackItem['controlId'] === config.controlId &&
                                    feedbackItem['instanceId'] !== config.instanceId;
                                // 2. when it's the same dialog, but not in a same control, and the count is equal to 0
                                const condition2 = feedbackItem['controlId'] !== config.controlId &&
                                    feedbackItem['instanceId'] === config.instanceId;
                                // 3. when it's not the same dialog and control
                                const condition3 = feedbackItem['controlId'] !== config.controlId && feedbackItem['instanceId'] !== config.instanceId;
                                // 4. when it's the same dialog and the control
                                // it stands that the same control will create a new contact from the same dialog
                                // and it will allow user to create again
                                const condition4 = feedbackItem['controlId'] === config.controlId && feedbackItem['instanceId'] === config.instanceId;
                                if (condition1 || condition2 || condition3) {
                                    this._store.dispatch({
                                        type: ADD_DUPLICATED_CONTACT,
                                        payload: {
                                            controlId: config.controlId,
                                            instanceId: config.instanceId,
                                            openCount: 1
                                        }
                                    });
                                    isNeedAdded = true;
                                }
                                if (condition4) {
                                    isNeedAdded = true;
                                }
                            }
                        } else {
                            this._store.dispatch({
                                type: ADD_DUPLICATED_CONTACT,
                                payload: {
                                    controlId: config.controlId,
                                    instanceId: config.instanceId,
                                    openCount: 1
                                }
                            });
                            isNeedAdded = true;
                        }
                        if (isNeedAdded) {
                            this._openDynamicComponentService.feedbackSubject$.next(entityBack);
                        }
                    });
                }
            });
    }

    trackByFn(index) {
        return index;
    }
}
